import { useState } from "react";
import { UserCircleIcon, PhotoIcon } from "@heroicons/react/20/solid";
import { useNavigate } from "react-router-dom";
import { addDoc, collection, getDocs, getFirestore, limit, query, where } from "firebase/firestore";
import { app } from "../firebase";
/**
 * Create a new organization with required fields.
 **/
export default function CreateOrganization({ user }) {
  const [address, setAddress] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [type, setType] = useState("vasi"); // Default to vasi
  const [error, setError] = useState("");

  const nav = useNavigate();

  /**
   * Validate email format
   **/
  function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  /**
   * Validate phone number format
   **/
  function isValidPhone(phone) {
    return /^\+?[\d\s-()]{10,}$/.test(phone);
  }

  function deriveCasePrefix(orgName) {
    const tokens = (orgName || "").match(/[A-Za-z0-9]+/g) || [];
    const initials = tokens.map((token) => token[0].toUpperCase()).join("");
    return initials || "ORG";
  }

  async function ensureUniqueCasePrefix(db, basePrefix) {
    for (let suffix = 1; suffix < 10000; suffix++) {
      const candidate = suffix === 1 ? basePrefix : `${basePrefix}${suffix}`;

      const snakeCaseQuery = query(
        collection(db, "organizations"),
        where("case_prefix", "==", candidate),
        limit(1)
      );
      const camelCaseQuery = query(
        collection(db, "organizations"),
        where("casePrefix", "==", candidate),
        limit(1)
      );

      const [snakeCaseSnapshot, camelCaseSnapshot] = await Promise.all([
        getDocs(snakeCaseQuery),
        getDocs(camelCaseQuery),
      ]);

      if (snakeCaseSnapshot.empty && camelCaseSnapshot.empty) {
        return candidate;
      }
    }

    throw new Error("Unable to generate a unique case prefix");
  }

  /**
   * Publish the organization to Firestore.
   **/
  async function createOrganization(evt) {
    evt.preventDefault();
    setError("");

    // Validate all required fields
    if (!name.trim()) {
      setError("Organization name is required");
      return;
    }
    if (!email.trim()) {
      setError("Email is required");
      return;
    }
    if (!isValidEmail(email)) {
      setError("Please enter a valid email address");
      return;
    }
    if (!phone.trim()) {
      setError("Phone number is required");
      return;
    }
    if (!isValidPhone(phone)) {
      setError("Please enter a valid phone number");
      return;
    }
    if (!address.trim()) {
      setError("Address is required");
      return;
    }
    if (!["vasi", "coachfax"].includes(type)) {
      setError("Invalid organization type");
      return;
    }

    // Get the DB reference
    const db = getFirestore(app);

    try {
      const basePrefix = deriveCasePrefix(name.trim());
      const casePrefix = await ensureUniqueCasePrefix(db, basePrefix);

      await addDoc(collection(db, "organizations"), {
        name: name.trim(),
        address: address.trim(),
        email: email.trim().toLowerCase(),
        phone: phone.trim(),
        type: type,
        case_prefix: casePrefix,
        createdAt: new Date().toISOString()
      });
      nav("/organizations");
    } catch (e) {
      setError(e.message || "Failed to create organization");
    }
  }

  return (
    <form onSubmit={createOrganization}>
      <div className="space-y-12">
        <div className="border-b border-gray-900/10 pb-12">
          <h2 className="text-base font-semibold leading-7 text-gray-900">Create Organization</h2>
        </div>

        {error !== "" && (
          <div className="rounded-md bg-red-50 p-4">
            <div className="text-sm text-red-700">{error}</div>
          </div>
        )}

        <div className="border-b border-gray-900/10 pb-12">
          <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
            <div className="sm:col-span-4">
              <label htmlFor="name" className="block text-sm font-medium leading-6 text-gray-900">
                Organization Name *
              </label>
              <div className="mt-2">
                <input
                  id="name"
                  name="name"
                  required
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                  onChange={(evt) => setName(evt.target.value)}
                  value={name}
                />
              </div>
            </div>

            <div className="sm:col-span-4">
              <label htmlFor="email" className="block text-sm font-medium leading-6 text-gray-900">
                Email *
              </label>
              <div className="mt-2">
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                  onChange={(evt) => setEmail(evt.target.value)}
                  value={email}
                />
              </div>
            </div>

            <div className="sm:col-span-4">
              <label htmlFor="phone" className="block text-sm font-medium leading-6 text-gray-900">
                Phone Number *
              </label>
              <div className="mt-2">
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  required
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                  onChange={(evt) => setPhone(evt.target.value)}
                  value={phone}
                />
              </div>
            </div>

            <div className="sm:col-span-4">
              <label htmlFor="type" className="block text-sm font-medium leading-6 text-gray-900">
                Organization Type *
              </label>
              <div className="mt-2">
                <select
                  id="type"
                  name="type"
                  required
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                  onChange={(evt) => setType(evt.target.value)}
                  value={type}
                >
                  <option value="vasi">VASI</option>
                  <option value="coachfax">CoachFax</option>
                </select>
              </div>
            </div>

            <div className="col-span-full">
              <label htmlFor="street-address" className="block text-sm font-medium leading-6 text-gray-900">
                Full Street Address *
              </label>
              <div className="mt-2">
                <input
                  type="text"
                  name="street-address"
                  id="street-address"
                  required
                  autoComplete="street-address"
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                  onChange={(evt) => setAddress(evt.target.value)}
                  value={address}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 flex items-center justify-end gap-x-6">
        <button
          type="button"
          onClick={() => nav("/organizations")}
          className="text-sm font-semibold leading-6 text-gray-900"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
        >
          Create Organization
        </button>
      </div>
    </form>
  );
}
